/**
 * Thin axios client for snapshot-api's per-org scheduled-snapshot endpoints,
 * consumed by the admin portal (staff-only).
 *
 * Mirrors src/api/erp-integration-client.ts:
 *   - module-scoped memoized axios instance
 *   - baseURL + internal-auth Bearer token injected via `configureClient`
 *   - `useSnapshotClient` hook returns a stable reference
 *
 * Cross-org access: snapshot-api resolves the target org from the
 * `x-epilot-org-id` request header, overriding the token's own org
 * (see snapshot-api src/index.ts registerSecurityHandler). NOTE the header is
 * `x-epilot-org-id`, NOT `x-ivy-org-id` (which erp-integration-api uses) — the
 * snapshot-api security handler only honors `x-epilot-org-id`. Every method
 * therefore accepts an explicit `orgId` and injects that header. Without it,
 * calls would operate on the admin-portal org instead of the target org.
 *
 * Types mirror snapshot-api openapi.yml (schemas OrgSnapshotSchedule,
 * PutOrgSnapshotScheduleRequest, RetentionConfig).
 */
import axios, { AxiosInstance } from 'axios'
import { useMemo } from 'react'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

// --- types ------------------------------------------------------------------

export interface RetentionConfig {
  value: number
  unit: 'days' | 'weeks' | 'months'
}

export interface OrgSnapshotSchedule {
  org_id: string
  enabled: boolean
  cron_expression: string
  timezone: string
  retention: RetentionConfig
  excluded_types?: string[]
  schedule_name: string
  last_started_at?: string
  last_completed_at?: string
  last_status?: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface PutOrgSnapshotScheduleRequest {
  enabled?: boolean
  cron_expression?: string
  timezone?: string
  retention?: RetentionConfig
  excluded_types?: string[]
}

// --- memoized axios factory -------------------------------------------------

let client: AxiosInstance | undefined

const getAxios = (): AxiosInstance => {
  if (!client) {
    client = axios.create({
      baseURL: config.SNAPSHOT
    })

    configureClient(client, {
      baseURL: config.SNAPSHOT
    })
  }

  return client
}

const withOrgHeader = (orgId: string) => ({
  headers: { 'x-epilot-org-id': orgId }
})

// --- public API -------------------------------------------------------------

export const getSnapshotClient = () => {
  const c = getAxios()

  return {
    /** Returns the org's schedule, or `null` when not yet enrolled (404). */
    getOrgSnapshotSchedule: async (
      orgId: string
    ): Promise<OrgSnapshotSchedule | null> => {
      try {
        const response = await c.get<OrgSnapshotSchedule>(
          '/v1/org-snapshot-schedule',
          withOrgHeader(orgId)
        )

        return response.data
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null
        }

        throw error
      }
    },

    putOrgSnapshotSchedule: async (
      orgId: string,
      body: PutOrgSnapshotScheduleRequest
    ): Promise<OrgSnapshotSchedule> => {
      const response = await c.put<OrgSnapshotSchedule>(
        '/v1/org-snapshot-schedule',
        body,
        withOrgHeader(orgId)
      )

      return response.data
    }
  }
}

export const useSnapshotClient = () => useMemo(() => getSnapshotClient(), [])
